import {
  DEFAULT_PROJECT_LEVEL_OPTIONS,
  PROJECT_LEVEL_KEYS,
  normalizeProjectLevels
} from '../project/project_level_constants.js?v=20260706d';
import { normalizeOptions as normalizeThreeInsulationOptions } from './three_insulation_options.js?v=20260706d';

const ENVIRONMENT_PRESETS = Object.freeze({
  datacenter: Object.freeze({
    id: 'datacenter',
    label: 'Datacenter',
    shortLabel: 'DB/DD',
    description: 'Doppelboden und technische Decke',
    levels: Object.freeze({
      ub: 600,
      db: 40,
      rh: 2700,
      dp: 40,
      dd: 300,
      floorGridSize: 600,
      floorColor: 'white',
      floorType: 'concrete_floor',
      ceilingType: 'datacenter_technical'
    }),
    options: Object.freeze({
      ub: Object.freeze({ includeInCalculation: true, showIn3d: true }),
      db: Object.freeze({ includeInCalculation: true, showIn3d: true }),
      rh: Object.freeze({ includeInCalculation: true, showIn3d: false }),
      dp: Object.freeze({ includeInCalculation: true, showIn3d: true }),
      dd: Object.freeze({ includeInCalculation: true, showIn3d: true })
    }),
    insulation: Object.freeze({
      wall: Object.freeze({ enabled: true, opacity: 0.5 }),
      ceiling: Object.freeze({ enabled: false, thickness: 80, opacity: 0.34 })
    })
  }),
  basement: Object.freeze({
    id: 'basement',
    label: 'Innenbereich',
    shortLabel: 'Beton',
    description: 'Betonboden, Betondecke und Deckenisolation',
    levels: Object.freeze({
      ub: 0,
      db: 0,
      rh: 2400,
      dp: 80,
      dd: 0,
      floorType: 'concrete_floor',
      ceilingType: 'concrete'
    }),
    options: Object.freeze({
      ub: Object.freeze({ includeInCalculation: false, showIn3d: false }),
      db: Object.freeze({ includeInCalculation: false, showIn3d: false }),
      rh: Object.freeze({ includeInCalculation: true, showIn3d: false }),
      dp: Object.freeze({ includeInCalculation: true, showIn3d: true }),
      dd: Object.freeze({ includeInCalculation: false, showIn3d: false })
    }),
    insulation: Object.freeze({
      wall: Object.freeze({ enabled: true, opacity: 0.56 }),
      ceiling: Object.freeze({ enabled: true, thickness: 80, opacity: 0.34 })
    })
  }),
  outdoor: Object.freeze({
    id: 'outdoor',
    label: 'Aussenbereich',
    shortLabel: 'Himmel',
    description: 'Boden ohne Decke',
    levels: Object.freeze({
      ub: 0,
      db: 0,
      rh: 2600,
      dp: 0,
      dd: 0,
      floorType: 'stein1',
      ceilingType: 'sky'
    }),
    options: Object.freeze({
      ub: Object.freeze({ includeInCalculation: false, showIn3d: false }),
      db: Object.freeze({ includeInCalculation: false, showIn3d: false }),
      rh: Object.freeze({ includeInCalculation: true, showIn3d: false }),
      dp: Object.freeze({ includeInCalculation: false, showIn3d: false }),
      dd: Object.freeze({ includeInCalculation: false, showIn3d: false })
    }),
    insulation: Object.freeze({
      wall: Object.freeze({ enabled: false, opacity: 0.56 }),
      ceiling: Object.freeze({ enabled: false, thickness: 80, opacity: 0.34 })
    })
  }),
  custom: Object.freeze({
    id: 'custom',
    label: 'Individuell',
    shortLabel: 'Frei',
    description: 'Frei definierbare Ebenen- und Umgebungseinstellungen',
    levels: Object.freeze({}),
    options: Object.freeze({})
  })
});

function getEnvironmentPresetOptions() {
  return Object.values(ENVIRONMENT_PRESETS);
}

function getEnvironmentPreset(id) {
  return ENVIRONMENT_PRESETS[String(id || '').trim().toLowerCase()] || null;
}

function applyEnvironmentPreset(appState, presetId) {
  const preset = getEnvironmentPreset(presetId);
  if (!preset || !appState?.project) return null;

  const current = normalizeProjectLevels(appState.project.levels);
  if (preset.id === 'custom') {
    appState.project.levels = normalizeProjectLevels({
      ...current,
      environmentPreset: preset.id
    });
    return preset;
  }

  const next = normalizeProjectLevels({
    ...current,
    ...preset.levels,
    rh: current.rh,
    options: mergeLevelOptions(current.options, preset.options),
    environmentPreset: preset.id
  });
  appState.project.levels = next;

  if (preset.insulation) {
    if (!appState.view3d || typeof appState.view3d !== 'object') appState.view3d = {};
    const currentInsulation = appState.view3d.insulation || {};
    const mergedInsulation = {
      ...currentInsulation,
      wall: { ...(currentInsulation.wall || {}), ...(preset.insulation.wall || {}) },
      ceiling: { ...(currentInsulation.ceiling || {}), ...(preset.insulation.ceiling || {}) }
    };
    appState.view3d.insulation = normalizeThreeInsulationOptions(mergedInsulation);
  }

  return preset;
}

function mergeLevelOptions(current = {}, preset = {}) {
  const merged = {};
  for (const key of PROJECT_LEVEL_KEYS) {
    merged[key] = {
      ...(current[key] || DEFAULT_PROJECT_LEVEL_OPTIONS[key] || {}),
      ...(preset[key] || {})
    };
  }
  return merged;
}

export {
  applyEnvironmentPreset,
  getEnvironmentPreset,
  getEnvironmentPresetOptions
};
